/*
 * ============================================================
 * argile-product.js — comportement de la fiche produit (hero/sticky)
 * ============================================================
 * Convention Argile : « 1 section = son JS ». Chargé par
 * `sections/argile-product.liquid`.
 *
 * Gère, par racine `[data-argile-product]` :
 *   - le stepper de quantité (− / valeur / +)
 *   - le toggle « Tester / Acheter la couleur » (bascule visuelle + mode testeur)
 *   - les VRAIES variantes Finition × Litrage : sélection en cascade, résolution
 *     de la variante, prix par variante + prix-au-litre dérivé
 *   - l'ajout au panier (AJAX → tiroir) et « Acheter maintenant »
 *
 * Variantes lues dans `<script type="application/json" data-argile-variants>`.
 * Idempotent + ré-init sur `shopify:section:load` (éditeur de thème).
 */
(function () {
  'use strict';

  /*
   * Formate des centimes selon la devise du MARCHÉ courant.
   *
   * L'euro est traité à part et rendu à l'identique de l'origine — « 57,33€ », virgule
   * décimale, SANS espace avant le symbole, conforme à la DA. `Intl.NumberFormat`
   * insérerait une espace insécable en fr-FR : ce serait une régression visible sur
   * tout le catalogue français, qui porte l'intégralité du chiffre d'affaires.
   *
   * La livre suit la convention britannique : symbole devant, point décimal.
   *
   * Toute autre devise retombe sur Intl — aucun marché n'est dans ce cas aujourd'hui,
   * mais un « € » codé en dur est précisément ce qui a produit le bug de la mise en
   * ligne UK (montant britannique affiché avec le symbole français).
   */
  var CURRENCY = 'EUR';

  function formatCents(cents) {
    if (cents == null || isNaN(cents)) return '';
    var v = (cents / 100).toFixed(2);
    if (CURRENCY === 'EUR') return v.replace('.', ',') + '€';
    if (CURRENCY === 'GBP') return '£' + v;
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency: CURRENCY }).format(cents / 100);
    } catch (e) {
      return v + ' ' + CURRENCY;
    }
  }

  /* Litrage label « 0,75 L » / « 2,5 L » → litres (Number), ou NaN. */
  function litresFromLabel(label) {
    if (!label) return NaN;
    var m = String(label).replace(',', '.').match(/([\d.]+)/);
    return m ? parseFloat(m[1]) : NaN;
  }

  /* Normalise un libellé de finition pour comparaison robuste (calculatrice ↔
     valeurs d'option) : minuscules, sans accents, sans le mot « argile ». */
  function normalizeFin(s) {
    return String(s || '')
      .toLowerCase()
      .normalize('NFD').replace(/[̀-ͯ]/g, '')
      .replace(/\bargile\b/g, '')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function initProduct(root) {
    if (!root || root.dataset.argileProductReady === 'true') return;
    root.dataset.argileProductReady = 'true';

    // Devise du marché, posée par argile-product.liquid. Une seule par page : le
    // sélecteur de pays recharge la page, donc pas de bascule à chaud à gérer.
    if (root.dataset.currency) CURRENCY = root.dataset.currency;

    /* ---- Galerie unifiée : slider piloté par vignettes (desktop) ET dots/flèches (mobile) ----
       Vignettes et dots portent tous deux [data-gallery-dot] + data-index → on synchronise par
       data-index (pas par position dans la NodeList) pour que les deux jeux restent cohérents.
       L'axe dépend du contexte : vertical sur la fiche teinte en desktop, horizontal partout ailleurs. */
    var galleryTrack = root.querySelector('[data-gallery-track]');
    var galleryDots = root.querySelectorAll('[data-gallery-dot]');
    if (galleryTrack && galleryDots.length) {
      /* Nombre réel de slides = enfants de la piste (et non le total des contrôles). */
      var slideCount = galleryTrack.children.length || 1;

      /* Axe de défilement. La fiche teinte (layout « scroll ») glisse VERTICALEMENT sur desktop —
         l'image suivante arrive par le bas, dans le sens de la molette. Partout ailleurs
         (mobile, template complémentaire « thumbnails »), la piste reste horizontale.
         Doit rester aligné sur le CSS, qui bascule la piste en colonne au même breakpoint. */
      var galleryWrap = root.querySelector('.argile-product__gallery');
      var scrollLayout = galleryWrap && galleryWrap.classList.contains('argile-product__gallery--scroll');
      var desktopMq = window.matchMedia('(min-width: 990px)');
      var isVertical = function () { return scrollLayout && desktopMq.matches; };
      /* Taille d'un cran = la dimension de la piste sur l'axe actif. */
      var slideSize = function () { return isVertical() ? galleryTrack.clientHeight : galleryTrack.clientWidth; };
      var scrollPos = function () { return isVertical() ? galleryTrack.scrollTop : galleryTrack.scrollLeft; };
      var currentIndex = function () {
        var s = slideSize();
        return s ? Math.round(scrollPos() / s) : 0;
      };
      var goToSlide = function (i) {
        var s = slideSize();
        if (!s) return;
        galleryTrack.scrollTo(isVertical() ? { top: i * s, behavior: 'smooth' } : { left: i * s, behavior: 'smooth' });
      };

      var activeIndex = 0;
      var setActiveDot = function (i) {
        activeIndex = i;
        galleryDots.forEach(function (d) {
          var active = (parseInt(d.dataset.index, 10) || 0) === i;
          d.classList.toggle('is-active', active);
          d.setAttribute('aria-selected', active ? 'true' : 'false');
          /* Le contrôle actif visible reste dans sa rangée (vignettes desktop) — sans saut vertical. */
          if (active && d.offsetParent && d.scrollIntoView) {
            d.scrollIntoView({ block: 'nearest', inline: 'nearest' });
          }
        });
      };
      galleryDots.forEach(function (dot) {
        dot.addEventListener('click', function () {
          goToSlide(parseInt(dot.dataset.index, 10) || 0);
        });
      });

      /* Au franchissement du breakpoint, la piste change d'axe (CSS) : la position de scroll de
         l'ancien axe ne veut plus rien dire. On réaligne sur l'image courante, sans animation. */
      if (scrollLayout && desktopMq.addEventListener) {
        desktopMq.addEventListener('change', function () {
          var i = activeIndex;
          var s = slideSize();
          if (!s) return;
          galleryTrack.scrollTo(isVertical() ? { top: i * s } : { left: i * s });
        });
      }

      var scrollScheduled = false;
      galleryTrack.addEventListener('scroll', function () {
        if (scrollScheduled) return;
        scrollScheduled = true;
        window.requestAnimationFrame(function () {
          scrollScheduled = false;
          if (!slideSize()) return;
          setActiveDot(currentIndex());
        });
      });

      /* Flèches précédent / suivant : défile d'une image (avec rebouclage). */
      var galleryStep = function (dir) {
        if (!slideSize()) return;
        goToSlide((currentIndex() + dir + slideCount) % slideCount);
      };
      var galleryPrev = root.querySelector('[data-gallery-prev]');
      var galleryNext = root.querySelector('[data-gallery-next]');
      if (galleryPrev) galleryPrev.addEventListener('click', function () { galleryStep(-1); });
      if (galleryNext) galleryNext.addEventListener('click', function () { galleryStep(1); });

      /* Desktop, galerie « scroll » (fiche teinte) : la molette sur le visuel snappe d'une image
         à l'autre (façon Zara Home), qui glisse verticalement. Aux extrémités on laisse la page
         défiler normalement. Gated : desktop uniquement + layout « scroll ». */
      if (scrollLayout && slideCount > 1) {
        var wheelLock = false;
        galleryTrack.addEventListener('wheel', function (e) {
          if (!isVertical()) return;                               // desktop + layout « scroll »
          if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) return;     // swipe horizontal → scroll natif
          if (!slideSize()) return;
          var next = currentIndex() + (e.deltaY > 0 ? 1 : -1);
          if (next < 0 || next > slideCount - 1) return;           // extrémité → la page défile
          e.preventDefault();                                      // sinon la piste défile nativement
          if (wheelLock) return;                                   // 1 cran = 1 image
          wheelLock = true;
          goToSlide(next);
          window.setTimeout(function () { wheelLock = false; }, 500);
        }, { passive: false });
      }
    }

    /* ---- Mobile : galerie intercalée entre la description et le bloc d'achat (cf. maquette) ---- */
    var galleryEl = root.querySelector('.argile-product__gallery');
    var galleryGrid = root.querySelector('.argile-product__grid');
    var galleryAnchor = root.querySelector('.argile-product__modes') || root.querySelector('.argile-product__form');
    if (galleryEl && galleryGrid && galleryAnchor) {
      var galleryMq = window.matchMedia('(max-width: 989px)');
      var placeGallery = function (mq) {
        if (mq.matches) {
          if (galleryEl.nextElementSibling !== galleryAnchor) {
            galleryAnchor.parentNode.insertBefore(galleryEl, galleryAnchor);
          }
        } else if (galleryGrid.firstElementChild !== galleryEl) {
          galleryGrid.insertBefore(galleryEl, galleryGrid.firstChild);
        }
      };
      placeGallery(galleryMq);
      galleryMq.addEventListener('change', placeGallery);
    }

    var qtyInput = root.querySelector('[data-qty-input]');

    /* ---- Stepper de quantité ---- */
    root.querySelectorAll('[data-qty-step]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        if (!qtyInput) return;
        var delta = parseInt(btn.dataset.qtyStep, 10) || 0;
        var next = Math.max(1, (parseInt(qtyInput.value, 10) || 1) + delta);
        qtyInput.value = next;
        updateTotals();
      });
    });
    if (qtyInput) {
      qtyInput.addEventListener('input', updateTotals);
      qtyInput.addEventListener('change', updateTotals);
    }

    /* ---- Variantes réelles (Finition × Litrage) ---- */
    var hiddenId = root.querySelector('input[name="id"]');
    var finitionSelect = root.querySelector('[data-finition]');
    var litrageGroup = root.querySelector('[data-litrage-group]');
    var priceEl = root.querySelector('[data-price]');
    var perUnitEl = root.querySelector('[data-price-per-unit]');

    var variants = [];
    var variantsEl = root.querySelector('[data-argile-variants]');
    if (variantsEl) {
      try { variants = JSON.parse(variantsEl.textContent) || []; } catch (e) { variants = []; }
    }

    function litrageButtons() {
      return litrageGroup ? Array.prototype.slice.call(litrageGroup.querySelectorAll('[data-litrage]')) : [];
    }
    function activeLitrage() {
      var b = litrageGroup ? litrageGroup.querySelector('[data-litrage].is-active:not([hidden])') : null;
      return b ? b.dataset.litrageValue : null;
    }
    function currentFinition() {
      return finitionSelect ? finitionSelect.value : null;
    }
    function variantFor(finition, litrage) {
      for (var i = 0; i < variants.length; i++) {
        var v = variants[i];
        if ((finition == null || v.finition === finition) && (litrage == null || v.litrage === litrage)) return v;
      }
      return null;
    }

    /* Cascade : restreint les litrages au choix de finition, auto-sélection du 1er dispo.

       Deux notions distinctes, à ne pas confondre (elles l'étaient avant le 2026-07-20,
       ce qui vidait le bloc « Conditionnement » sur une teinte en rupture) :
       - EXISTE   : la finition courante propose ce litrage. Sinon → masqué, ce n'est
                    pas un produit.
       - DISPONIBLE : ce litrage est vendable. Sinon → affiché mais barré, comme les
                    échantillons. Masquer priverait le visiteur de l'information « ce
                    format existe mais il est épuisé », et sur une teinte entièrement
                    en rupture ne laissait qu'un libellé au-dessus du vide. */
    function syncLitrages() {
      if (!litrageGroup) return;
      var fin = currentFinition();
      // Pas de sélecteur Finition (page complémentaire, show_finition off) → fin == null :
      // on ne filtre pas par finition, sinon tous les litrages seraient masqués.
      var pourFinition = variants.filter(function (v) { return fin == null || v.finition === fin; });
      var existe = pourFinition.map(function (v) { return v.litrage; });
      var disponible = pourFinition.filter(function (v) { return v.available; }).map(function (v) { return v.litrage; });

      var firstAvailable = null;
      var firstExisting = null;
      var keepActive = false;
      litrageButtons().forEach(function (btn) {
        var val = btn.dataset.litrageValue;
        var ok = existe.indexOf(val) !== -1;
        var dispo = disponible.indexOf(val) !== -1;
        btn.hidden = !ok;
        btn.classList.toggle('is-unavailable', ok && !dispo);
        if (ok && firstExisting === null) firstExisting = btn;
        if (dispo && firstAvailable === null) firstAvailable = btn;
        if (ok && btn.classList.contains('is-active')) keepActive = true;
      });
      /* Priorité au premier litrage VENDABLE : ouvrir la fiche sur un format épuisé
         alors qu'un autre est disponible serait un faux négatif. Repli sur le premier
         existant quand tout est épuisé, pour ne pas laisser le groupe sans sélection. */
      if (!keepActive) setActiveLitrage(firstAvailable || firstExisting);
    }
    function setActiveLitrage(btn) {
      litrageButtons().forEach(function (b) {
        var on = b === btn;
        b.classList.toggle('is-active', on);
        b.setAttribute('aria-checked', on ? 'true' : 'false');
      });
    }

    /* Résout la variante courante → hidden id + prix. */
    function syncVariant() {
      if (root.dataset.mode === 'test') return; // mode Tester géré séparément (U4)
      var v = variantFor(currentFinition(), activeLitrage());
      if (!v) return;
      if (hiddenId) hiddenId.value = v.id;
      root.dataset.variantId = v.id;
      if (priceEl) priceEl.textContent = formatCents(v.price);
      if (perUnitEl) {
        var litres = litresFromLabel(v.litrage);
        perUnitEl.textContent = litres > 0 ? formatCents(v.price / litres) + ' / L' : '';
      }
      updateTotals();
      applyAvailability();
    }

    if (finitionSelect) {
      finitionSelect.addEventListener('change', function () { syncLitrages(); syncVariant(); });
    }
    litrageButtons().forEach(function (btn) {
      btn.addEventListener('click', function () {
        if (btn.hidden) return;
        setActiveLitrage(btn);
        syncVariant();
      });
    });

    /* ---- Toggle Tester / Acheter ---- */
    root.querySelectorAll('.argile-product__mode[data-mode]').forEach(function (tab) {
      tab.addEventListener('click', function () {
        root.dataset.mode = tab.dataset.mode;
        root.querySelectorAll('.argile-product__mode[data-mode]').forEach(function (t) {
          t.classList.toggle('is-active', t === tab);
          t.setAttribute('aria-selected', t === tab ? 'true' : 'false');
        });
        syncMode();
      });
    });

    /* Mode Tester : choix de l'échantillon (testeur 125 ml / nuancier A4). */
    var addBtn = root.querySelector('.argile-product__add');
    var addLabelBuy = addBtn ? addBtn.textContent : '';
    var addLabelSoldOut = root.dataset.soldoutLabel || 'Épuisé';
    var addLabelSoldOutSample = root.dataset.soldoutSampleLabel || addLabelSoldOut;
    var otherTeintes = root.querySelector('[data-other-teintes]');
    var sampleGroup = root.querySelector('[data-sample-group]');

    /* Disponibilité de la résolution courante.
       - Acheter : la variante pot résolue doit être available (rupture = false).
       - Tester  : la disponibilité vient de `data-sample-available`, posé par le Liquid
         sur chaque bouton d'échantillon.

       ATTENTION (2026-07-20) — ce chemin retournait `true` en dur en mode Tester, en
       s'appuyant sur un invariant du Liquid : un échantillon épuisé n'était pas rendu,
       donc un échantillon présent était forcément vendable. Cet invariant est levé
       depuis que le client peut épuiser le testeur ou le nuancier A4 seuls : l'échantillon
       est maintenant rendu même épuisé, pour que sa rupture se voie au lieu de faire
       disparaître un bouton. Réintroduire le raccourci ici produirait un bouton
       « Ajouter au panier » actif sur une variante invendable. */
    function currentResolvedAvailable() {
      if (root.dataset.mode === 'test') {
        var s = activeSample();
        /* Pas de bouton d'échantillon du tout = rien à vendre en mode Tester. */
        if (!s) return false;
        return s.dataset.sampleAvailable !== 'false';
      }
      var v = variantFor(currentFinition(), activeLitrage());
      return !!(v && v.available);
    }

    /* Reflète la rupture : boutons grisés/inopérants + repli « autres teintes ».
       Appelée en fin de syncVariant()/syncMode(), après que le libellé normal a
       été posé — on ne l'écrase qu'en cas de rupture. */
    function applyAvailability() {
      var ok = currentResolvedAvailable();
      var isTest = root.dataset.mode === 'test';
      root.classList.toggle('is-soldout', !ok);
      if (addBtn) {
        addBtn.disabled = !ok;
        /* Libellé distinct en mode Tester : « Épuisé » seul laisserait croire que c'est
           la teinte entière qui est en rupture, alors que seul l'échantillon l'est. */
        if (!ok) addBtn.textContent = isTest ? addLabelSoldOutSample : addLabelSoldOut;
      }
      if (buyNow) buyNow.disabled = !ok;
      if (otherTeintes) otherTeintes.hidden = ok;
    }

    /* Finition dont aucun litrage n'est disponible -> option non sélectionnable. */
    function markUnavailableFinitions() {
      if (!finitionSelect) return;
      Array.prototype.slice.call(finitionSelect.options).forEach(function (opt) {
        var hasAvail = variants.some(function (v) { return v.finition === opt.value && v.available; });
        opt.disabled = !hasAvail;
      });
    }

    function sampleButtons() {
      return sampleGroup ? Array.prototype.slice.call(sampleGroup.querySelectorAll('[data-sample]')) : [];
    }
    function activeSample() {
      return (sampleGroup && sampleGroup.querySelector('[data-sample].is-active')) || sampleButtons()[0] || null;
    }
    sampleButtons().forEach(function (btn) {
      btn.addEventListener('click', function () {
        sampleButtons().forEach(function (b) {
          var on = b === btn;
          b.classList.toggle('is-active', on);
          b.setAttribute('aria-checked', on ? 'true' : 'false');
        });
        if (root.dataset.mode === 'test') syncMode();
      });
    });

    function syncMode() {
      if (root.dataset.mode === 'test') {
        var s = activeSample();
        if (s) {
          if (hiddenId) hiddenId.value = s.dataset.sampleId;
          root.dataset.variantId = s.dataset.sampleId;
          if (priceEl) priceEl.textContent = formatCents(parseInt(s.dataset.samplePrice, 10));
          if (perUnitEl) perUnitEl.textContent = '';
          if (addBtn) addBtn.textContent = s.dataset.sampleLabel || addLabelBuy;
        }
        updateTotals();
        applyAvailability();
      } else {
        if (addBtn) addBtn.textContent = addLabelBuy;
        syncVariant();
      }
    }

    /* ---- Total « Acheter maintenant » = prix unitaire × qté ---- */
    var buyNow = root.querySelector('[data-buy-now]');
    var buyNowTotal = root.querySelector('[data-buy-now-total]');

    function currentUnitCents() {
      if (root.dataset.mode === 'test') {
        var s = activeSample();
        return s ? parseInt(s.dataset.samplePrice, 10) : NaN;
      }
      var v = variantFor(currentFinition(), activeLitrage());
      return v ? v.price : NaN;
    }
    function updateTotals() {
      if (!buyNowTotal) return;
      var qty = qtyInput ? Math.max(1, parseInt(qtyInput.value, 10) || 1) : 1;
      var total = currentUnitCents() * qty;
      buyNowTotal.textContent = isNaN(total) ? '' : '(' + formatCents(total) + ')';
    }

    /* ---- Propriétés de ligne d'un échantillon ----
       Le nom de la teinte, visible au panier, au checkout, dans le mail et l'admin
       (« Teinte : PONCE CLAIRE »), et son handle, caché (préfixe `_`), qui sert à la
       ligne de panier pour renvoyer à la page de la teinte (snippets/cart-products.liquid).
       Seulement quand la variante ajoutée est l'échantillon actif du mode Tester : un pot
       n'emporte rien. Un attribut absent (HTML en cache d'avant la mise en ligne) → aucune
       propriété plutôt qu'une propriété vide. */
    function sampleProperties(variantId) {
      if (root.dataset.mode !== 'test') return null;
      var s = activeSample();
      var label = root.dataset.teinteLabel;
      var title = root.dataset.teinteTitle;
      var handle = root.dataset.teinteHandle;
      if (!s || s.dataset.sampleId !== String(variantId) || !label || !title || !handle) return null;
      var props = {};
      props[label] = title;
      props._teinte_handle = handle;
      return props;
    }
    function addBody(variantId, qty) {
      var body = { id: variantId, quantity: qty };
      var props = sampleProperties(variantId);
      if (props) body.properties = props;
      return body;
    }

    /* ---- « Acheter maintenant » : ajout AJAX puis checkout ---- */
    if (buyNow) {
      buyNow.addEventListener('click', function () {
        var variantId = root.dataset.variantId;
        if (!variantId || !currentResolvedAvailable()) return;
        var qty = qtyInput ? Math.max(1, parseInt(qtyInput.value, 10) || 1) : 1;
        buyNow.setAttribute('aria-busy', 'true');
        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(addBody(variantId, qty)),
        })
          .then(function () { window.location.href = '/checkout'; })
          .catch(function () { buyNow.removeAttribute('aria-busy'); });
      });
    }

    /* ---- Ajout au panier : loading sur le bouton, puis ouverture du tiroir
       une fois le panier ET le cross-sell prêts. On n'ouvre pas un panel
       incomplet (évite le flicker « items sans cross-sell -> panel complet ») :
       le bouton montre un spinner pendant le fetch + le (re)chargement des
       recos, et le tiroir s'ouvre déjà complet. ---- */
    var form = root.querySelector('.argile-product__form');
    if (form) {
      form.addEventListener('submit', function (event) {
        event.preventDefault();
        var variantId = root.dataset.variantId;
        if (!variantId || !currentResolvedAvailable()) return;
        var qty = qtyInput ? Math.max(1, parseInt(qtyInput.value, 10) || 1) : 1;

        // Retour immédiat : spinner sur le bouton (le tiroir s'ouvrira après).
        if (addBtn) {
          addBtn.classList.add('is-loading');
          addBtn.setAttribute('aria-busy', 'true');
        }

        // Ids des sections cart-items -> morph propre du tiroir (pas de re-fetch).
        var sectionIds = [];
        document.querySelectorAll('cart-items-component').forEach(function (el) {
          if (el.dataset && el.dataset.sectionId) sectionIds.push(el.dataset.sectionId);
        });

        // Ouvre le tiroir + stoppe le spinner. Appelé une seule fois, soit quand
        // le cross-sell a fini de (re)charger (event `argile:recos-ready`), soit
        // au timeout de secours si les recos tardent. trigger.click() ouvre via
        // /open (idempotent si déjà ouvert).
        var opened = false;
        function openDrawer() {
          if (opened) return;
          opened = true;
          document.removeEventListener('argile:recos-ready', openDrawer);
          if (addBtn) {
            addBtn.classList.remove('is-loading');
            addBtn.removeAttribute('aria-busy');
          }
          var trigger = document.querySelector('[data-testid="cart-drawer-trigger"]');
          if (trigger) trigger.click();
        }

        var addUrl = (window.Theme && window.Theme.routes && window.Theme.routes.cart_add_url) || '/cart/add';
        var body = addBody(variantId, qty);
        body.sections = sectionIds.join(',');
        fetch(addUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
          body: JSON.stringify(body),
        })
          .then(function (r) { return r.json(); })
          .then(function (res) {
            return fetch('/cart.js').then(function (r) { return r.json(); }).then(function (cart) {
              // On attend la fin du (re)chargement du cross-sell avant d'ouvrir,
              // pour que le panel apparaisse complet d'un coup. Timeout de secours.
              document.addEventListener('argile:recos-ready', openDrawer);
              setTimeout(openDrawer, 1200);
              // Event natif (nom = ThemeEvents.cartUpdate). On fournit les sections
              // rendues pour que cart-items morphe sans re-fetch -> tiroir stable.
              // Cet event déclenche aussi le rechargement du cross-sell.
              document.dispatchEvent(new CustomEvent('cart:update', {
                bubbles: true,
                detail: {
                  resource: cart,
                  sourceId: 'argile-product',
                  data: { source: 'argile-product', itemCount: cart.item_count, sections: res.sections },
                },
              }));
            });
          })
          .catch(function () {
            // En cas d'échec réseau : on ne laisse pas le bouton bloqué.
            openDrawer();
          });
      });
    }

    /* ---- Init : aligne la sélection sur la variante (deep-link ?variant= prioritaire) ---- */
    function selectVariantObj(v) {
      if (!v) return;
      if (finitionSelect) finitionSelect.value = v.finition;
      syncLitrages();
      litrageButtons().forEach(function (b) {
        if (!b.hidden && b.dataset.litrageValue === v.litrage) setActiveLitrage(b);
      });
    }
    if (variants.length) {
      markUnavailableFinitions();
      var wantVid = new URLSearchParams(window.location.search).get('variant');
      var deepLinked = wantVid && variants.filter(function (v) { return String(v.id) === String(wantVid); })[0];
      if (deepLinked) {
        /* ?variant= (présent sur tous les liens catalogue) → on pré-sélectionne la
           variante mais on NE force PAS « Acheter » : le mode par défaut (Tester) est
           respecté. Le forçage « Acheter » reste réservé au retour calculatrice (qfin/qlit/qqty). */
        selectVariantObj(deepLinked);
      } else {
        selectVariantObj(variantFor(currentFinition(), null) || variants[0]);
      }
      syncVariant();
      applyQuantitePrefill();
    }
    updateTotals();
    applyAvailability();

    /* État initial du toggle Tester/Acheter : le défaut serveur peut être « Tester »
       (teinte avec échantillon). On aligne l'UI des onglets sur le mode courant
       (?variant= a pu forcer « Acheter ») et on lance syncMode() si « Tester » pour
       appliquer libellé + prix de l'échantillon dès le chargement. */
    root.querySelectorAll('.argile-product__mode[data-mode]').forEach(function (t) {
      var on = t.dataset.mode === root.dataset.mode;
      t.classList.toggle('is-active', on);
      t.setAttribute('aria-selected', on ? 'true' : 'false');
    });
    if (root.dataset.mode === 'test') syncMode();

    /* Pré-remplissage depuis la calculatrice de quantité (renvoi modale produit) :
       params ?qfin=<finition>&qlit=<litres>&qqty=<nb> → sélectionne finition +
       litrage recommandé + quantité, puis remonte au panneau d'achat. */
    function applyQuantitePrefill() {
      var qp = new URLSearchParams(window.location.search);
      var qfin = qp.get('qfin'), qlit = qp.get('qlit'), qqty = qp.get('qqty');
      if (!qfin && !qlit && !qqty) return;
      root.dataset.mode = 'buy';
      root.querySelectorAll('.argile-product__mode[data-mode]').forEach(function (t) {
        var on = t.dataset.mode === 'buy';
        t.classList.toggle('is-active', on);
        t.setAttribute('aria-selected', on ? 'true' : 'false');
      });
      if (qfin && finitionSelect) {
        var want = normalizeFin(qfin);
        Array.prototype.slice.call(finitionSelect.options).forEach(function (o) {
          if (normalizeFin(o.value) === want) finitionSelect.value = o.value;
        });
      }
      syncLitrages();
      if (qlit) {
        var wantL = parseFloat(String(qlit).replace(',', '.'));
        litrageButtons().forEach(function (b) {
          if (!b.hidden && Math.abs(litresFromLabel(b.dataset.litrageValue) - wantL) < 1e-6) setActiveLitrage(b);
        });
      }
      syncVariant();
      if (qqty && qtyInput) { qtyInput.value = Math.max(1, parseInt(qqty, 10) || 1); updateTotals(); }
      try { root.scrollIntoView({ behavior: 'smooth', block: 'start' }); } catch (e) {}
    }
  }

  function initAll(scope) {
    (scope || document)
      .querySelectorAll('[data-argile-product]')
      .forEach(initProduct);
  }

  /* Pont guide de finition → dropdown : le guide (argile-finition-finder.js)
     émet « argile:finition-chosen » quand l'utilisateur choisit une finition.
     On aligne le <select data-finition> de la fiche + on relance la cascade
     (litrage/variante/prix) via un change natif. Si la finition n'existe pas
     pour la teinte affichée : preventDefault → le guide affiche une note. */
  document.addEventListener('argile:finition-chosen', function (e) {
    var name = e.detail && e.detail.name;
    if (!name) return;
    var sel = document.querySelector('[data-argile-product] [data-finition]');
    if (!sel) return; // pas de fiche produit sur la page → le guide reste no-op
    var want = normalizeFin(name);
    var match = Array.prototype.slice.call(sel.options).filter(function (o) {
      return normalizeFin(o.value) === want;
    })[0];
    if (!match) { e.preventDefault(); return; } // indispo pour cette teinte
    sel.value = match.value;
    sel.dispatchEvent(new Event('change', { bubbles: true }));
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(document); });
  } else {
    initAll(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });
})();
